import DataTable from '@/common/tableData/DataTable';
import useTournamentDetails from '@/hooks/useTournamentDetails';
import { format } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge } from 'reactstrap';

const TournamentEvents = () => {
  const { tournamentId } = useParams();

  if (!tournamentId) {
    return null;
  }

  const { tournamentEvents, refetchTournamentEvents } = useTournamentDetails(tournamentId);

  // Compute newest first ordering
  const orderedEvents = useMemo(() => {
    if (!tournamentEvents) return [];
    return [...tournamentEvents].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [tournamentEvents]);

  useEffect(() => {
    setTimeout(() => {
      refetchTournamentEvents();
    }, 30000);
  }, []);

  return (
    <div className="d-flex flex-column w-100 gap-4">
      <div className="d-flex flex-column w-100 gap-2">
        <h4 className="text-lg font-bold">Event logs</h4>
        <div className="d-flex w-100 justify-content-start align-items-center flex-row gap-4">
          <div className="w-100 flex-1">
            <DataTable
              columns={[
                {
                  header: '',
                  accessorKey: 'index',
                  size: 20,
                  cell: ({ row }) => <span className="opacity-50">{row.index + 1}</span>,
                },
                {
                  header: 'Events',
                  accessorKey: 'eventType',
                  size: 130,
                  cell: ({ row }) => {
                    const formattedEventType = row?.original?.eventType.replace(/_/g, ' ');

                    const getBadgeColor = (event: string) => {
                      const upper = event.toUpperCase();
                      if (upper.includes('TABLE') && upper.includes('OPEN')) return 'info';
                      if (upper.includes('TABLE') && upper.includes('CLOSE')) return 'secondary';
                      if (upper.includes('START') || upper.includes('INIT')) return 'success';
                      if (upper.includes('ELIMINATED') || upper.includes('END') || upper.includes('CANCEL'))
                        return 'danger';
                      if (upper.includes('BREAK') || upper.includes('PAUSE') || upper.includes('LEVEL'))
                        return 'warning';
                      return 'primary';
                    };

                    return (
                      <Badge
                        className="d-flex align-items-center justify-content-center px-2 py-1 text-center"
                        color={getBadgeColor(formattedEventType)}
                        style={{ minWidth: '160px', height: '30px', whiteSpace: 'normal' }}
                      >
                        {formattedEventType ?? '-'}
                      </Badge>
                    );
                  },
                },
                {
                  header: 'Time',
                  accessorKey: 'createdAt',
                  cell: ({ row }) => {
                    return format(new Date(row.original.createdAt), 'yyyy-MM-dd HH:mm:ss');
                  },
                },
                {
                  header: 'Message',
                  accessorKey: 'message',
                  size: 400,
                  cell: ({ row }) => {
                    return (
                      <div className="text-wrap">
                        <p className="mb-0">{row?.original?.message}</p>
                      </div>
                    );
                  },
                },
                {
                  header: 'Data',
                  accessorKey: 'eventData',
                  size: 30,
                  cell: ({ row }) => {
                    const data = row?.original?.eventData;
                    if (!data) return <p className="mb-0"></p>;

                    const handleCopy = () => {
                      navigator.clipboard.writeText(JSON.stringify(data));
                      toast.success('Event data copied to clipboard');
                    };

                    return (
                      <span className="cursor-pointer" onClick={handleCopy}>
                        <i className="ri-file-copy-2-line"></i>
                      </span>
                    );
                  },
                },
              ]}
              enableSorting={false}
              pageSize={50}
              data={orderedEvents}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentEvents;
